import React, { useState, useRef, useEffect } from 'react';
import { useQuery } from 'react-query';
import {
  XMarkIcon,
  ChevronDownIcon,
  CheckCircleIcon,
} from '@heroicons/react/24/outline';
import { Spinner } from './Spinner';
import { brandService, userService } from '../services/api';

// Who a given role reports to — drives the "Assign Under" dropdown.
const PARENT_ROLE = { sales_executive: 'dealer', dealer: 'admin' };

// Immediate parent role — who else (besides super_admin) can edit a given
// role's email. Mirrors backend/routes/users.js EMAIL_EDITOR_ROLE.
const EMAIL_EDITOR_ROLE = {
  sales_executive: 'dealer',
  dealer:          'admin',
  admin:           'super_admin',
};

// Roles a given actor role is allowed to assign
const ASSIGNABLE_ROLES = {
  super_admin: [
    { value: 'admin', label: 'Admin' },
    { value: 'dealer', label: 'Dealer' },
    { value: 'sales_executive', label: 'Sales Executive' },
  ],
  admin: [
    { value: 'dealer', label: 'Dealer' },
    { value: 'sales_executive', label: 'Sales Executive' },
  ],
  dealer: [
    { value: 'sales_executive', label: 'Sales Executive' },
  ],
};

// ── Multi-select dropdown (mirrors the AddUser one) ───────────────────────────
function MultiSelectDropdown({ options, selected, onChange, placeholder = 'Select…' }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const toggle = (id) => {
    onChange(selected.includes(id) ? selected.filter(x => x !== id) : [...selected, id]);
  };

  const selectedLabels = options.filter(o => selected.includes(o.value)).map(o => o.label);

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        className="input flex items-center justify-between w-full text-left cursor-pointer"
      >
        <div className="flex flex-wrap gap-1 min-h-[1.25rem]">
          {selectedLabels.length === 0 ? (
            <span className="text-gray-400">{placeholder}</span>
          ) : (
            selectedLabels.map((l, i) => (
              <span key={i} className="inline-flex items-center gap-1 px-2 py-0.5 bg-primary-50 text-primary-700 text-xs font-medium rounded">
                {l}
                <span
                  role="button"
                  tabIndex={0}
                  onClick={(e) => {
                    e.stopPropagation();
                    const opt = options.find(o => o.label === l);
                    if (opt) toggle(opt.value);
                  }}
                  className="hover:text-primary-900 cursor-pointer"
                >×</span>
              </span>
            ))
          )}
        </div>
        <ChevronDownIcon className={`h-4 w-4 text-gray-400 flex-shrink-0 ml-2 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>

      {open && (
        <div className="absolute z-30 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-y-auto">
          {options.length === 0 ? (
            <div className="px-3 py-2 text-sm text-gray-400">No options</div>
          ) : (
            options.map(opt => {
              const isSelected = selected.includes(opt.value);
              return (
                <label
                  key={opt.value}
                  className="flex items-center gap-2 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 cursor-pointer"
                >
                  <input
                    type="checkbox"
                    checked={isSelected}
                    onChange={() => toggle(opt.value)}
                    className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                  />
                  <span className="flex-1">{opt.label}</span>
                  {isSelected && <CheckCircleIcon className="h-4 w-4 text-primary-500" />}
                </label>
              );
            })
          )}
        </div>
      )}
    </div>
  );
}

export const EditUserModal = ({ user, currentUserRole, onSave, onClose }) => {
  const [formData, setFormData] = useState({
    firstName: user.firstName,
    lastName: user.lastName || '',
    email: user.email || '',
    phone: user.phone || '',
    role: user.role,
    status: user.status,
    brands: Array.isArray(user.brands) ? user.brands.map(b => (typeof b === 'string' ? b : b._id)) : [],
    parentId: (typeof user.parentId === 'object' ? user.parentId?._id : user.parentId) || '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');

  const availableRoles = ASSIGNABLE_ROLES[currentUserRole] || [];
  const canEditEmail = currentUserRole === 'super_admin' || currentUserRole === EMAIL_EDITOR_ROLE[user.role];

  // "Assign Under" only makes sense for roles that report to a pickable
  // parent (dealer → admin, sales executive → dealer), and a dealer editor
  // has no one else in scope to move their team to.
  const parentRole = PARENT_ROLE[formData.role];
  const canReassign = !!parentRole && currentUserRole !== 'dealer';

  const { data: hierarchyData } = useQuery(
    'hierarchyUsers',
    () => userService.getHierarchy(),
    { staleTime: 5 * 60 * 1000, enabled: canReassign }
  );
  const parentOptions = (hierarchyData?.data?.data?.users || hierarchyData?.data?.users || [])
    .filter(u => u.role === parentRole && u.status === 'active' && String(u._id) !== String(user._id));

  const { data: brandsData } = useQuery(
    'brandsForUserForm',
    () => brandService.getBrands(),
    { staleTime: 10 * 60 * 1000, enabled: formData.role === 'dealer' }
  );
  const brandsList = brandsData?.data?.data?.brands || brandsData?.data?.brands || [];

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (formData.role === 'dealer' && (!formData.brands || formData.brands.length === 0)) {
      setError('Please select at least one brand for this dealer');
      return;
    }
    setIsSubmitting(true);
    try {
      await onSave(user._id, formData);
      onClose();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to update user');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4">
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75" onClick={onClose} />
        <div className="relative bg-white rounded-lg shadow-xl w-full max-w-md p-6 max-h-[90vh] overflow-y-auto">
          <div className="flex items-start justify-between mb-4">
            <div>
              <h3 className="text-lg font-medium text-gray-900">
                Edit User — {user.firstName} {user.lastName}
              </h3>
              {user.userCode && (
                <p className="mt-0.5 text-sm text-gray-500">
                  User ID: <span className="font-mono text-gray-700">{user.userCode}</span>
                </p>
              )}
            </div>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
              <XMarkIcon className="h-5 w-5" />
            </button>
          </div>

          {error && (
            <div className="mb-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded text-sm">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="label">First Name</label>
                <input
                  type="text"
                  required
                  value={formData.firstName}
                  onChange={(e) => setFormData(p => ({ ...p, firstName: e.target.value }))}
                  className="input"
                />
              </div>
              <div>
                <label className="label">Last Name <span className="text-xs font-normal text-gray-400">(optional)</span></label>
                <input
                  type="text"
                  value={formData.lastName}
                  onChange={(e) => setFormData(p => ({ ...p, lastName: e.target.value }))}
                  className="input"
                />
              </div>
            </div>

            <div>
              <label className="label">Email</label>
              <input
                type="email"
                required
                disabled={!canEditEmail}
                value={formData.email}
                onChange={(e) => setFormData(p => ({ ...p, email: e.target.value }))}
                className={`input ${!canEditEmail ? 'bg-gray-50 text-gray-500 cursor-not-allowed' : ''}`}
              />
              {!canEditEmail && (
                <p className="mt-1 text-xs text-gray-500">
                  Only a {EMAIL_EDITOR_ROLE[user.role]?.replace('_', ' ') || 'super admin'} or higher can change this user's email.
                </p>
              )}
            </div>

            <div>
              <label className="label">Phone</label>
              <input
                type="tel"
                value={formData.phone}
                onChange={(e) => setFormData(p => ({ ...p, phone: e.target.value }))}
                className="input"
              />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="label">Role</label>
                <select
                  value={formData.role}
                  onChange={(e) => setFormData(p => ({ ...p, role: e.target.value }))}
                  className="input"
                >
                  {availableRoles.map(r => (
                    <option key={r.value} value={r.value}>{r.label}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="label">Status</label>
                <select
                  value={formData.status}
                  onChange={(e) => setFormData(p => ({ ...p, status: e.target.value }))}
                  className="input"
                >
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                  <option value="suspended">Suspended</option>
                </select>
              </div>
            </div>

            {/* Assign Under — move this user (and their team) to a different parent */}
            {canReassign && (
              <div>
                <label className="label">Assign Under</label>
                <select
                  value={formData.parentId}
                  onChange={(e) => setFormData(p => ({ ...p, parentId: e.target.value }))}
                  className="input"
                >
                  <option value="">— Select {parentRole === 'admin' ? 'Admin' : 'Dealer'} —</option>
                  {parentOptions.map(u => (
                    <option key={u._id} value={u._id}>{u.firstName} {u.lastName}</option>
                  ))}
                </select>
                <p className="mt-1 text-xs text-gray-500">
                  Changing this moves the user and everyone under them to the new reporting line.
                </p>
              </div>
            )}

            {/* Brands — only when role is dealer */}
            {formData.role === 'dealer' && (
              <div>
                <label className="label">
                  Brands <span className="text-red-500">*</span>
                </label>
                {brandsList.length === 0 ? (
                  <p className="text-sm text-gray-400 italic">No brands configured. Add brands from Settings → Brands & Models.</p>
                ) : (
                  <MultiSelectDropdown
                    options={brandsList.filter(b => b.isActive !== false).map(b => ({ value: b._id, label: b.name }))}
                    selected={formData.brands}
                    onChange={(next) => setFormData(p => ({ ...p, brands: next }))}
                    placeholder="Select brands…"
                  />
                )}
                <p className="mt-1 text-xs text-gray-500">Select the brands this dealer represents.</p>
              </div>
            )}

            <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
              <button type="button" onClick={onClose} className="btn-outline-secondary" disabled={isSubmitting}>
                Cancel
              </button>
              <button type="submit" className="btn-primary" disabled={isSubmitting}>
                {isSubmitting ? <><Spinner size="sm" className="mr-2" />Saving...</> : 'Save Changes'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};
